/**
 * API n2nai_erpcoach (ERP Coach) — cùng contract với erpcoach-web/src/services/api.js
 * Gọi trực tiếp ERPCOACH_API_BASE_URL (không dùng JWT E-court).
 */
import { apiSlice } from './apiSlice'
import { ERPCOACH_API_BASE_URL, toErpcoachCoachStorageVideoId } from '../../config/erpcoach'

async function coachJson<T>(path: string, init?: RequestInit): Promise<{ data: T } | { error: { status: number; data: unknown } }> {
  const url = `${ERPCOACH_API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`
  try {
    const res = await fetch(url, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers as Record<string, string>),
      },
    })
    const text = await res.text()
    let parsed: unknown = null
    try {
      parsed = text ? JSON.parse(text) : null
    } catch {
      return { error: { status: res.status, data: text || 'Invalid JSON' } }
    }
    if (!res.ok) {
      return { error: { status: res.status, data: parsed ?? text } }
    }
    return { data: parsed as T }
  } catch (e) {
    return { error: { status: 0, data: e instanceof Error ? e.message : String(e) } }
  }
}

export interface ErpcoachSummaryLevel {
  label: string
  text: string
}

export interface ErpcoachQuizQuestion {
  question: string
  answers: string[]
  correctAnswer: string
  explanation?: string
}

export interface ErpcoachFlashcard {
  question: string
  answer: string
}

export interface ErpcoachHistoryItem {
  message: string
  response: string
  created_at?: string
  sources?: unknown[]
  timelines?: unknown[]
}

export interface ErpcoachChatPostBody {
  message: string
  video_id: string
  progress?: Record<string, unknown> | null
}

export interface ErpcoachChatPostResponse {
  response: string
  sources?: unknown[]
  timelines?: unknown[]
}

export const erpcoachCoachApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getErpcoachTranscript: builder.query<Record<string, unknown>, string>({
      queryFn: async (videoId) => coachJson<Record<string, unknown>>(`/transcript/${encodeURIComponent(videoId)}`),
    }),
    getErpcoachSummaryLevels: builder.query<{ levels?: ErpcoachSummaryLevel[] }, string>({
      queryFn: async (videoId) =>
        coachJson<{ levels?: ErpcoachSummaryLevel[] }>(`/videos/${encodeURIComponent(videoId)}/summary/levels`),
    }),
    getErpcoachSummaryBullets: builder.query<{ bullets?: { text?: string }[] }, string>({
      queryFn: async (videoId) =>
        coachJson<{ bullets?: { text?: string }[] }>(`/videos/${encodeURIComponent(videoId)}/summary/bullets`),
    }),
    getErpcoachQuiz: builder.query<{ questions?: unknown[] }, string>({
      queryFn: async (videoId) => {
        const r = await coachJson<{ questions?: unknown[] }>(`/videos/${encodeURIComponent(videoId)}/quiz`)
        if ('error' in r) return r
        return { data: r.data ?? { questions: [] } }
      },
    }),
    getErpcoachFlashcards: builder.query<{ flashcards?: ErpcoachFlashcard[] }, string>({
      queryFn: async (videoId) => {
        const r = await coachJson<{ flashcards?: ErpcoachFlashcard[] }>(`/videos/${encodeURIComponent(videoId)}/flashcards`)
        if ('error' in r) return r
        return { data: r.data ?? { flashcards: [] } }
      },
    }),
    getErpcoachVideoHistory: builder.query<ErpcoachHistoryItem[], string>({
      queryFn: async (videoId) => {
        const r = await coachJson<ErpcoachHistoryItem[] | { results?: ErpcoachHistoryItem[] }>(
          `/videos/${encodeURIComponent(videoId)}/history`
        )
        if ('error' in r) return r
        const d = r.data
        if (Array.isArray(d)) return { data: d }
        if (d && typeof d === 'object' && 'results' in d && Array.isArray((d as { results: ErpcoachHistoryItem[] }).results)) {
          return { data: (d as { results: ErpcoachHistoryItem[] }).results }
        }
        return { data: [] }
      },
    }),
    postErpcoachChat: builder.mutation<ErpcoachChatPostResponse, ErpcoachChatPostBody>({
      queryFn: async (body) => {
        const r = await coachJson<ErpcoachChatPostResponse>('/chat', {
          method: 'POST',
          body: JSON.stringify({
            message: body.message,
            video_id: toErpcoachCoachStorageVideoId(body.video_id),
            progress: body.progress ?? null,
          }),
        })
        return r
      },
    }),
  }),
})

export const {
  useGetErpcoachTranscriptQuery,
  useGetErpcoachSummaryLevelsQuery,
  useGetErpcoachSummaryBulletsQuery,
  useGetErpcoachQuizQuery,
  useGetErpcoachFlashcardsQuery,
  useGetErpcoachVideoHistoryQuery,
  usePostErpcoachChatMutation,
} = erpcoachCoachApi
